// ============================================================
// ソナーページ — 常用ページ (v2 stacked sonar)
// ============================================================
// 左テキスト (1 container) + 右 image (200×100 を縦 2 枚 = 200×200 効果) で
// 1 ページを構成。
// 起動時に createStartUpPageContainer で投入し、以後 textContainerUpgrade /
// updateImageRawData で部分更新する。

import {
  CreateStartUpPageContainer,
  RebuildPageContainer,
  TextContainerProperty,
  ImageContainerProperty,
  ImageRawDataUpdate,
  TextContainerUpgrade,
  type EvenAppBridge,
} from '@evenrealities/even_hub_sdk'
import type { NearbyState } from '../types'
import { SonarRenderer, type StackedFrame } from '../render/sonar-renderer'
import { composeLeftPanel } from '../render/spotlight-composer'
import { distanceMeters } from '../util/distance'
import { hash32 } from '../util/hash'
import { fetchTrainInformation, type OdptTrainInformation } from '../data/odpt-client'
import { OPERATORS } from '../config'

const ALERTS_CACHE_TTL_MS = 60_000
const ALERTS_REFRESH_INTERVAL_MS = 60_000

// container IDs (page 内で unique)
const ID_BG          = 1
const ID_LEFT        = 2   // 左カラム全部 (header / time / bus / train / summary)
const ID_SONAR_TOP   = 10
const ID_SONAR_BOT   = 11

const N_BG         = 'bg'
const N_LEFT       = 'lt'
const N_SONAR_TOP  = 'son_t'
const N_SONAR_BOT  = 'son_b'

// 画像配置: 200×100 ×2 を縦に貼って 200×200 効果。凡例は画像内に描画。
const SONAR_X = 376
const SONAR_TOP_Y = 36
const SONAR_BOT_Y = SONAR_TOP_Y + 100   // 136

export class SonarPage {
  private renderer = new SonarRenderer()
  private lastHash = { top: 0, bottom: 0 }
  private lastTexts: Record<string, string> = {}
  private inFlightImage = false
  private pendingFrame: StackedFrame | null = null
  private firstInstall = true   // SDK: createStartUpPageContainer は起動時 1 回のみ
  // 運行情報 (alert)。undefined = 未取得 / null = 平常運転 / string = アラート本文
  private latestAlertText: string | null | undefined = undefined
  private alertsFetchedAt = 0
  private alertsInFlight = false

  constructor(private bridge: EvenAppBridge) {}

  async install(initial: NearbyState): Promise<void> {
    const leftText   = composeLeftPanel({
      date: new Date(),
      radiusM: initial.radiusM,
      headingDeg: initial.fix.heading,
      spotlightBus: initial.spotlightBus,
      spotlightTrain: initial.spotlightTrain,
      summary: this.summaryInputs(initial),
      alertText: this.latestAlertText,
    })
    // 初回 install 時に運行情報を fetch (非同期、次の update で反映)
    void this.refreshAlerts()

    const bg = new TextContainerProperty({
      xPosition: 0, yPosition: 0, width: 576, height: 288,
      borderWidth: 0, borderColor: 0, paddingLength: 0,
      containerID: ID_BG, containerName: N_BG,
      content: ' ',
      isEventCapture: 1,
    })

    // Layout (canvas 576 × 288):
    //   left   (0, 12, 376, 268)   全部まとめ
    //   sonar_top (376, 36, 200, 100)
    //   sonar_bot (376, 136, 200, 100)
    //   legend    (376, 238, 200, 28)
    const left = new TextContainerProperty({
      xPosition: 0, yPosition: 12, width: 376, height: 268,
      borderWidth: 0, borderColor: 0, paddingLength: 6,
      containerID: ID_LEFT, containerName: N_LEFT,
      content: leftText,
      isEventCapture: 0,
    })
    const sonarTop = new ImageContainerProperty({
      xPosition: SONAR_X, yPosition: SONAR_TOP_Y,
      width: 200, height: 100,
      containerID: ID_SONAR_TOP, containerName: N_SONAR_TOP,
    })
    const sonarBot = new ImageContainerProperty({
      xPosition: SONAR_X, yPosition: SONAR_BOT_Y,
      width: 200, height: 100,
      containerID: ID_SONAR_BOT, containerName: N_SONAR_BOT,
    })

    const total = 1 /* bg */ + 1 /* left */ + 2 /* images */
    const payload = {
      containerTotalNum: total,
      textObject: [bg, left],
      imageObject: [sonarTop, sonarBot],
    }
    if (this.firstInstall) {
      await this.bridge.createStartUpPageContainer(new CreateStartUpPageContainer(payload))
      this.firstInstall = false
    } else {
      // 切り替えで戻ってきたとき: createStartUpPageContainer は 1 度しか使えない
      await this.bridge.rebuildPageContainer(new RebuildPageContainer(payload))
    }
    // ハッシュをリセット (rebuild 後は前のフレームが消えているので強制再送)
    this.lastHash = { top: 0, bottom: 0 }

    this.lastTexts = {
      [N_LEFT]: leftText,
    }

    // 初回フレームを送る
    await this.update(initial)
  }

  /** state 変更時に呼ぶ */
  async update(state: NearbyState): Promise<void> {
    // ── text 更新 ──
    const leftText = composeLeftPanel({
      date: new Date(),
      radiusM: state.radiusM,
      headingDeg: state.fix.heading,
      spotlightBus: state.spotlightBus,
      spotlightTrain: state.spotlightTrain,
      summary: this.summaryInputs(state),
      alertText: this.latestAlertText,
    })
    await this.upgradeIfChanged(ID_LEFT, N_LEFT, leftText)
    // 運行情報の rolling 取得 (キャッシュ TTL を超えていれば更新)
    if (Date.now() - this.alertsFetchedAt > ALERTS_REFRESH_INTERVAL_MS) {
      void this.refreshAlerts()
    }

    // ── image 更新 (top + bottom を直列に送る) ──
    const frame = await this.renderer.render(state)
    this.pendingFrame = frame
    if (!this.inFlightImage) void this.drainImage()
  }

  // ───────── private ─────────

  private async refreshAlerts(): Promise<void> {
    if (this.alertsInFlight) return
    this.alertsInFlight = true
    try {
      const a = await fetchTrainInformation([
        OPERATORS.toei, OPERATORS.tokyoMetro, OPERATORS.jrEast,
        OPERATORS.twr, OPERATORS.tx, OPERATORS.kantoRailway,
      ])
      const active = a.find((x: OdptTrainInformation) => {
        const s = x['odpt:trainInformationStatus']?.ja
        return s && s !== '平常運転' && s !== ''
      })
      if (active) {
        const text = active['odpt:trainInformationText']?.ja
        this.latestAlertText = text && text.length > 0 ? text : null
      } else {
        this.latestAlertText = null
      }
      this.alertsFetchedAt = Date.now()
    } catch {
      // silent — auth エラー等で取れなくても画面は出すべき
    } finally {
      this.alertsInFlight = false
    }
  }

  private summaryInputs(state: NearbyState) {
    const stations = state.nearby.filter((e) => e.kind === 'station')
    let nearestStation: { name: string; distanceM: number; bearing: number } | undefined
    if (stations.length > 0) {
      const s = stations[0]
      const d = distanceMeters(state.fix, s)
      const θ = bearingFrom(state.fix.lat, state.fix.lon, s.lat, s.lon)
      nearestStation = { name: s.label ?? '駅', distanceM: d, bearing: θ }
    }
    const busstopCount = state.nearby.filter((e) => e.kind === 'busstop').length
    return { nearestStation, busstopCount, busstopRadiusM: 200 }
  }

  private async upgradeIfChanged(id: number, name: string, content: string): Promise<void> {
    if (this.lastTexts[name] === content) return
    try {
      await this.bridge.textContainerUpgrade(
        new TextContainerUpgrade({
          containerID: id,
          containerName: name,
          content,
          contentOffset: 0,
          contentLength: content.length,
        }),
      )
      this.lastTexts[name] = content
    } catch (err) {
      console.warn(`[page-sonar] textContainerUpgrade ${name} failed`, err)
    }
  }

  private async drainImage(): Promise<void> {
    this.inFlightImage = true
    try {
      while (this.pendingFrame) {
        const f = this.pendingFrame
        this.pendingFrame = null
        await this.sendHalf('top', ID_SONAR_TOP, N_SONAR_TOP, f.top)
        await this.sendHalf('bottom', ID_SONAR_BOT, N_SONAR_BOT, f.bottom)
      }
    } finally {
      this.inFlightImage = false
    }
  }

  private async sendHalf(
    half: 'top' | 'bottom',
    id: number, name: string, buf: Uint8Array,
  ): Promise<void> {
    const h = hash32(buf)
    if (h === this.lastHash[half]) return
    try {
      const r = await this.bridge.updateImageRawData(
        new ImageRawDataUpdate({
          containerID: id,
          containerName: name,
          imageData: buf,
        }),
      )
      if (r === 'success') {
        this.lastHash[half] = h
      } else {
        console.warn(`[page-sonar] updateImageRawData ${half}: ${r}`)
      }
    } catch (err) {
      console.warn(`[page-sonar] updateImageRawData ${half} failed`, err)
    }
  }
}

function bearingFrom(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = lat1 * Math.PI / 180
  const φ2 = lat2 * Math.PI / 180
  const Δλ = (lon2 - lon1) * Math.PI / 180
  const y = Math.sin(Δλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ)
  return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360
}
